import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer';

// --- Data & Helpers ---

interface RoomType {
    id: string;
    label: string;
    icon: string;
}

const roomTypes: RoomType[] = [
    { id: 'entrance', label: 'Main Entrance', icon: 'https://cdn-icons-png.flaticon.com/512/3277/3277103.png' },
    { id: 'kitchen', label: 'Kitchen', icon: 'https://cdn-icons-png.flaticon.com/512/2853/2853761.png' },
    { id: 'bedroom', label: 'Master Bedroom', icon: 'https://cdn-icons-png.flaticon.com/512/3030/3030336.png' },
    { id: 'toilet', label: 'Toilet/Bathroom', icon: 'https://cdn-icons-png.flaticon.com/512/2942/2942790.png' },
    { id: 'living', label: 'Living Room', icon: 'https://cdn-icons-png.flaticon.com/512/2590/2590525.png' },
];

const gridPositions = [
    { id: 'NE', label: 'NE', zone: 'Ishanya' }, { id: 'N', label: 'N', zone: 'Uttara' }, { id: 'NW', label: 'NW', zone: 'Vayavya' },
    { id: 'E', label: 'E', zone: 'Purva' }, { id: 'C', label: 'Center', zone: 'Brahmasthan' }, { id: 'W', label: 'W', zone: 'Pashchima' },
    { id: 'SE', label: 'SE', zone: 'Agneya' }, { id: 'S', label: 'S', zone: 'Dakshina' }, { id: 'SW', label: 'SW', zone: 'Nairutya' },
];

const scoringRules: Record<string, Record<string, number>> = {
    entrance: { N: 100, NE: 100, E: 90, NW: 70, W: 60, SE: 40, S: 20, SW: 10 },
    kitchen: { SE: 100, E: 80, NW: 70, W: 50, NE: 20, N: 10, S: 30, SW: 10 },
    bedroom: { SW: 100, S: 90, W: 80, NW: 60, N: 40, NE: 10, E: 30, SE: 20 },
    toilet: { NW: 100, W: 80, S: 70, SE: 40, SW: 10, N: 20, NE: 5, E: 20 },
    living: { N: 100, NE: 100, E: 90, NW: 80, W: 60, SE: 40, S: 30, SW: 20 },
};

const VastuCalculatorProcess: React.FC = () => {
    const [step, setStep] = useState(1);
    const [entrance, setEntrance] = useState<string>('');
    const [mapping, setMapping] = useState<Record<string, string>>({}); // roomId -> positionId
    const [activeRoom, setActiveRoom] = useState<string | null>(null);

    const handleEntranceSelect = (dir: string) => {
        setEntrance(dir);
        setStep(2);
    };

    const handleGridClick = (posId: string) => {
        if (!activeRoom) return;
        setMapping({ ...mapping, [activeRoom]: posId });
        setActiveRoom(null);
    };

    const calculateScore = () => {
        let total = 0;
        let count = 0;
        
        // Entrance score
        if (entrance) {
            total += scoringRules.entrance[entrance] || 0;
            count++;
        }
        
        // Mapped rooms score
        Object.entries(mapping).forEach(([roomId, posId]) => {
            total += scoringRules[roomId]?.[posId] || 0;
            count++;
        });

        return count > 0 ? Math.round(total / count) : 0;
    };

    const reset = () => {
        setStep(1);
        setEntrance('');
        setMapping({});
        setActiveRoom(null);
    };

    const getScoreCategory = (score: number) => {
        if (score >= 85) return { label: 'Excellent', color: 'text-emerald-700', bg: 'bg-emerald-50' };
        if (score >= 70) return { label: 'Good', color: 'text-blue-700', bg: 'bg-blue-50' };
        if (score >= 50) return { label: 'Average', color: 'text-amber-700', bg: 'bg-amber-50' };
        return { label: 'Incomplete / Poor', color: 'text-red-700', bg: 'bg-red-50' };
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            <Navbar transparent={false} />
            
            <main className="py-6 md:py-10 px-4 mt-2">
                <div className="max-w-6xl mx-auto">
                    {/* Header Section */}
                    <div className="text-center mb-10">
                        <motion.h1 
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-4xl md:text-5xl font-black text-black uppercase tracking-tight mb-2 italic"
                        >
                            Professional <span className="text-[#f1ae24]">Vastu</span> Analysis
                        </motion.h1>
                        <p className="text-black font-bold uppercase tracking-widest text-xs">Analyze energy flow and spatial harmony.</p>
</div>

                    <div className="bg-white rounded-[2.5rem] border-2 border-black p-6 md:p-12 shadow-2xl shadow-blue-900/5 relative overflow-hidden">
                        <AnimatePresence mode="wait">
                            {step === 1 && (
                                <motion.div 
                                    key="step1"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="text-center relative z-10"
                                >
                                    <div className="max-w-2xl mx-auto">
                                        <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-black italic">01</div>
                                        <h2 className="text-3xl font-black text-black uppercase italic mb-4">Set Your Property Orientation</h2>
                                        <p className="text-black font-bold text-lg mb-10">Which direction does your main entrance face? This sets the foundation for your analysis.</p>
                                        
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'].map((dir) => (
                                                <button 
                                                    key={dir}
                                                    onClick={() => handleEntranceSelect(dir)}
                                                    className="p-8 rounded-3xl border-2 border-black hover:bg-black hover:text-white transition-all group relative overflow-hidden shadow-sm"
                                                >
                                                    <span className="relative z-10 text-2xl font-black italic">{dir}</span>
                                                    <div className="absolute top-0 right-0 w-8 h-8 bg-[#f1ae24] -mr-4 -mt-4 rounded-full group-hover:scale-[10] transition-transform duration-700 opacity-20"></div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {step === 2 && (
                                <motion.div 
                                    key="step2"
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    className="relative z-10"
                                >
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                                        {/* Left Side: Room Selector */}
                                        <div className="lg:col-span-4 bg-gray-50 rounded-[2rem] p-8 border border-black/5">
                                            <div className="mb-8">
                                                <h3 className="text-xl font-black text-black uppercase italic mb-2">Assign Rooms</h3>
                                                <p className="text-black font-bold text-[10px] uppercase">Select a room and click a grid square.</p>
                                            </div>
                                            
                                            <div className="space-y-4">
                                                {roomTypes.map((room) => (
                                                    <button
                                                        key={room.id}
                                                        onClick={() => setActiveRoom(room.id)}
                                                        className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all group ${
                                                            activeRoom === room.id ? 'border-[#f1ae24] bg-white shadow-lg scale-105' : 'border-transparent bg-white shadow-sm hover:border-black'
                                                        } ${mapping[room.id] ? 'opacity-50' : 'opacity-100'}`}
                                                    >
                                                        <div className="flex items-center gap-4 text-left">
                                                            <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center p-2 border border-black/5">
                                                                <img src={room.icon} alt="" className="w-full h-full object-contain" />
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-black text-black uppercase leading-tight">{room.label}</p>
                                                                <p className="text-[9px] font-bold text-black uppercase tracking-tighter">
                                                                    {mapping[room.id] ? `Located: ${mapping[room.id]}` : 'Ready to map'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {mapping[room.id] ? (
                                                            <i className="fa-solid fa-circle-check text-emerald-500"></i>
                                                        ) : activeRoom === room.id ? (
                                                            <div className="w-2 h-2 rounded-full bg-[#f1ae24] animate-ping"></div>
                                                        ) : (
                                                            <i className="fa-solid fa-plus text-black/20 group-hover:text-black"></i>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>

                                            <button 
                                                onClick={() => setStep(3)}
                                                className="w-full mt-10 py-5 bg-black text-white rounded-full font-black uppercase tracking-widest text-xs hover:bg-[#f1ae24] transition-all"
                                            >
                                                Finish Analysis
                                            </button>
                                            <button onClick={() => setStep(1)} className="w-full mt-2 py-3 text-black font-black uppercase text-[9px] opacity-40 hover:opacity-100">Back to Step 1</button>
                                        </div>

                                        {/* Right Side: 3x3 Grid Interface */}
                                        <div className="lg:col-span-8 flex flex-col items-center">
                                            <div className="relative mb-8 flex items-center justify-center">
                                                {/* Compass Label N (Fixed at top for visualization) */}
                                                <div className="absolute top-[-40px] font-black text-black text-sm uppercase tracking-widest">Property North</div>
                                                
                                                <div className="grid grid-cols-3 gap-3 p-4 bg-gray-100/50 rounded-[2.5rem] border-2 border-black/10 shadow-inner w-full max-w-[500px] aspect-square relative">
                                                    {gridPositions.map((pos) => {
                                                        const roomInThisPos = Object.entries(mapping).find(([_, pId]) => pId === pos.id);
                                                        const roomData = roomInThisPos ? roomTypes.find(r => r.id === roomInThisPos[0]) : null;

                                                        return (
                                                            <button
                                                                key={pos.id}
                                                                onClick={() => handleGridClick(pos.id)}
                                                                className={`relative flex flex-col items-center justify-center p-4 rounded-[1.5rem] border-2 transition-all aspect-square ${
                                                                    roomInThisPos 
                                                                        ? 'bg-black text-white border-black shadow-xl ring-4 ring-[#f1ae24]/20' 
                                                                        : 'bg-white border-black/5 hover:border-black/20 hover:shadow-lg'
                                                                } ${activeRoom ? 'cursor-crosshair border-dashed border-[#f1ae24]/50 scale-95 hover:scale-100 active:bg-[#f1ae24]/5' : ''}`}
                                                            >
                                                                {roomData ? (
                                                                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex flex-col items-center gap-2">
                                                                        <img src={roomData.icon} alt="" className="w-8 h-8 md:w-12 md:h-12 brightness-0 invert" />
                                                                        <span className="text-[10px] md:text-xs font-black uppercase tracking-tighter leading-none">{roomData.label.split(' ')[0]}</span>
                                                                    </motion.div>
                                                                ) : (
                                                                    <>
                                                                        <span className="text-xl md:text-2xl font-black text-black italic opacity-20">{pos.label}</span>
                                                                        <span className="text-[8px] font-bold text-black absolute bottom-3 uppercase">{pos.zone}</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            
                                            {/* Energy Meter Tooltip */}
                                            <div className="bg-white border-2 border-black rounded-full px-8 py-4 flex items-center gap-6 shadow-xl">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center font-black italic text-black">{calculateScore()}</div>
                                                    <span className="text-[10px] font-black uppercase text-black italic">Vastu Score</span>
                                                </div>
                                                <div className="w-[2px] h-6 bg-black/10"></div>
                                                <div className="flex items-center gap-3">
                                                    <i className="fa-solid fa-bolt text-[#f1ae24]"></i>
                                                    <span className="text-[10px] font-black uppercase text-black">Property Orientation: {entrance}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {step === 3 && (
                                <motion.div 
                                    key="step3"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="text-center relative z-10"
                                >
                                    <div className="flex flex-col items-center mb-10">
                                        <div className="relative mb-8">
                                            <div className="w-32 h-32 md:w-48 md:h-48 rounded-full border-[10px] border-black flex flex-col items-center justify-center bg-white shadow-2xl relative z-10">
                                                <span className="text-5xl md:text-7xl font-black text-black italic leading-none">{calculateScore()}</span>
                                                <span className="text-[10px] font-black text-black uppercase tracking-widest mt-2">Compliance</span>
                                            </div>
                                            <div className="absolute top-0 left-0 w-full h-full animate-ping border-4 border-[#f1ae24]/20 rounded-full"></div>
                                        </div>
                                        
                                        <div className={`px-8 py-2 rounded-full border-2 border-black mb-4 font-black uppercase text-sm ${getScoreCategory(calculateScore()).bg}`}>
                                            Status: <span className={getScoreCategory(calculateScore()).color}>{getScoreCategory(calculateScore()).label}</span>
                                        </div>
                                        <h2 className="text-3xl font-black text-black uppercase italic mb-2">Final Energy Balance</h2>
                                        <p className="text-black font-bold text-lg mb-12 max-w-2xl mx-auto italic">Your property alignment reveals a compliance of {calculateScore()}%. Strategic shifts in key zones can further harmonize the energy flow.</p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
                                        {Object.entries(mapping).map(([roomId, posId]) => {
                                            const score = scoringRules[roomId]?.[posId] || 0;
                                            const room = roomTypes.find(r => r.id === roomId);
                                            return (
                                                <div key={roomId} className="bg-gray-50/50 rounded-2xl border-2 border-black/5 p-5 relative overflow-hidden group hover:border-black transition-all">
                                                    <div className="flex items-center gap-4 mb-4">
                                                        <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center p-3 border border-black/5 group-hover:scale-110 transition-transform">
                                                            <img src={room?.icon} alt="" className="w-full h-full object-contain" />
                                                        </div>
                                                        <div>
                                                            <h5 className="text-sm font-black text-black uppercase italic leading-none mb-1">{room?.label}</h5>
                                                            <span className="text-[9px] font-black uppercase text-black">Zone: {posId}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className={`text-[10px] font-black uppercase ${score >= 70 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                            {score >= 70 ? '✓ Auspicious' : '⚠ Remedies Needed'}
                                                        </span>
                                                        <div className="flex gap-1">
                                                            {[1, 2, 3, 4, 5].map((i) => (
                                                                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i * 20 <= score ? 'bg-black' : 'bg-black/10'}`}></div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="mt-12 flex flex-col md:flex-row gap-4 justify-center">
                                        <button onClick={reset} className="px-12 py-5 bg-black text-white rounded-full font-black uppercase tracking-[0.2em] text-xs hover:bg-[#f1ae24] transition-all hover:scale-105 active:scale-95 shadow-xl">Re-Calculate</button>
                                        <Link to="/contact" className="px-12 py-5 bg-white border-2 border-black text-black rounded-full font-black uppercase tracking-[0.2em] text-xs hover:bg-black hover:text-white transition-all hover:scale-105 active:scale-95 shadow-xl no-underline inline-block">Consult Vastu Expert</Link>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Background Decor */}
                        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#f1ae24]/5 rounded-full -ml-48 -mb-48 blur-3xl opacity-50"></div>
                        <div className="absolute top-1/2 left-0 w-2 h-32 bg-black -mt-16 ml-[-1px]"></div>
                        <div className="absolute top-1/2 right-0 w-2 h-32 bg-black -mt-16 mr-[-1px]"></div>
                    </div>

                    <div className="mt-12 text-center">
                        <p className="text-[9px] font-black uppercase tracking-[0.4em] text-black">Precision Analysis Engine • Ancient Wisdom Meets Modern AI • Disclaimer: Consultation Recommended</p>
                    </div>
                </div>
            </main>


            <Footer />
        </div>
    );
};

export default VastuCalculatorProcess;
